import {
    createContext,
    type PropsWithChildren,
    useCallback,
    useEffect,
    useContext,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
    useLayoutEffect,
} from 'react';
import type { SCAPIShopperBasketsV2Schemas } from '@/types/scapi';
import type { BasketSnapshot } from '@/middlewares/basket.server';
import { useScapiFetcher } from '@/hooks/use-scapi-fetcher';
import { useScapiFetcherEffect } from '@/hooks/use-scapi-fetcher-effect';
import { parseBasketCookie } from '@/lib/basket/cookie';
import { getMerchandiseItemCount, getMerchandiseProductItems } from '@/lib/cart-accessory-utils';

// Cookie changes are not observable via an event; the store returns a noop unsubscribe and relies on existing
// re-render triggers (updater callbacks on mutations) to refresh. useSyncExternalStore guarantees SSR (null) and
// client (cookie) snapshots can diverge without a hydration-mismatch warning.
// eslint-disable-next-line @typescript-eslint/no-empty-function
const subscribeBasketCookie = () => () => {};
const getServerBasketCookieSnapshot = (): BasketSnapshot | null => null;

export type BasketProviderValue = {
    snapshot?: BasketSnapshot | null;
    current?: SCAPIShopperBasketsV2Schemas['Basket'];
    /** Whether the full basket has been hydrated */
    hydrated?: boolean;
    /** Error encountered during hydration, if any */
    error?: string[] | null;
};

const defaultCreateSnapshot = (basket: SCAPIShopperBasketsV2Schemas['Basket']): BasketSnapshot => ({
    basketId: basket.basketId ?? '',
    totalItemCount: getMerchandiseItemCount(basket.productItems),
    uniqueProductCount: getMerchandiseProductItems(basket.productItems).length,
});

const getBasketItemCount = (basket: SCAPIShopperBasketsV2Schemas['Basket']): number =>
    getMerchandiseItemCount(basket.productItems);

/**
 * Returns true when a hydrated basket no longer matches the server snapshot
 * (e.g. after login/logout basket merge or cookie clear).
 */
const isBasketOutOfSync = (
    current: SCAPIShopperBasketsV2Schemas['Basket'] | undefined,
    snapshot: BasketSnapshot | null | undefined
): boolean => {
    if (!snapshot?.basketId) {
        return Boolean(current);
    }
    if (!current) {
        return false;
    }
    return (
        current.basketId !== snapshot.basketId ||
        getBasketItemCount(current) !== snapshot.totalItemCount ||
        getMerchandiseProductItems(current.productItems).length !== snapshot.uniqueProductCount
    );
};

/*
 * Shared basket context that exposes snapshot data, current basket data, and hydration state.
 * `hydrated` indicates whether a full basket load has been attempted.
 * `error` holds any hydration errors from the last attempt.
 */
const BasketContext = createContext<BasketProviderValue>({
    snapshot: undefined,
    current: undefined,
    hydrated: undefined,
    error: undefined,
});

type BasketUpdater = {
    setBasket: (next: BasketProviderValue | ((prev: BasketProviderValue | undefined) => BasketProviderValue)) => void;
    /**
     * Imperatively load the full basket. Safe to call from anywhere — the provider owns the underlying SCAPI fetcher
     * and dedupes by tracking the in-flight basket id. While a load is in flight, repeat calls are no-ops; on success
     * the loaded data is written to context (so callers gating on `current` short-circuit naturally); on failure the
     * in-flight ref is cleared so a retry can proceed.
     */
    loadBasket: () => void;
};

const BasketUpdaterContext = createContext<BasketUpdater | undefined>(undefined);

// Mini cart state lives in a separate context so toggling the sheet does not invalidate BasketUpdaterContext.
// Otherwise, every consumer of useBasket / useBasketUpdater / useBasketLoader / useBasketReset would re-render on
// every open/close (notably product cards across PLP/PDP that wire add-to-cart through useBasketUpdater).
type MiniCartProviderValue = {
    miniCartOpen: boolean;
    setMiniCartOpen: (open: boolean) => void;
};

const MiniCartContext = createContext<MiniCartProviderValue | undefined>(undefined);

/**
 * Provider for basket data that's typically retrieved by the basket middleware.
 * Exposes a setter so any component can update the basket state after lazy loads.
 *
 * ## Two-source state model
 *
 * The effective snapshot exposed on the context is derived from two independent inputs, merged in a single `useMemo`:
 *
 * 1. **Props** (`snapshot`, `basket`) — typically passed down from a route loader that reads the basket middleware's
 *    request-scoped context. Set on SSR and on every loader revalidation.
 * 2. **`__sfdc_basket` cookie** — written by the basket middleware's response step (`Set-Cookie`) and by client-side
 *    mutations. Read on the client via `useSyncExternalStore`. Treated as ground truth post-hydration: it overrides
 *    the prop snapshot whenever present.
 *
 * The merge rule is `cookieSnapshot ?? state.snapshot`, i.e. cookie wins whenever the visitor has one.
 *
 * ## Why two sources — enabling cache-safe SSR
 *
 * This split is what allows a caller to decide, per request, how much per-visitor state to bake into the SSR HTML.
 * Two scenarios the model is designed to support:
 *
 * - **Shared HTML caching.** If a route emits SSR HTML that may be served from a shared cache (e.g. a guest segment
 *   at a CDN), the caller can pass `snapshot={null}` so no per-visitor count is serialized. After hydration, the
 *   client reads the visitor's own cookie and fills the counter in — the cached HTML stays visitor-agnostic, but
 *   the UI still reflects the current visitor's basket.
 * - **Per-user or uncached HTML.** If the caller knows the response is per-user (uncached, or cached under a per-user
 *   key), it can pass the middleware snapshot directly. The badge renders with the correct count during SSR, no
 *   post-hydration flash.
 *
 * The provider itself is indifferent to which mode the caller picks; the contract is simply "whatever you pass as
 * `snapshot` ends up in the SSR HTML, and the cookie will augment or correct it on the client."
 *
 * ## SSR → hydrate → augment lifecycle
 *
 * The mechanism that makes the above possible is a single `useSyncExternalStore` call with asymmetric snapshot
 * functions:
 *
 *   - `getServerSnapshot` returns `null` — the value baked into SSR HTML.
 *   - `getSnapshot` reads `document.cookie` — available only on the client.
 *
 * React's `useSyncExternalStore` contract explicitly permits the server and client snapshots to diverge without
 * emitting a hydration-mismatch warning. It is the **only** hook that offers this guarantee; reaching for plain
 * `useEffect` + `useState` for the same trick would either warn on hydration or flicker a tick after first paint.
 *
 * Flow when the caller opts out of baking the snapshot (e.g. shared SSR HTML):
 *
 * ```
 *  SSR                         Hydration                    Post-hydration
 *  ───                         ─────────                    ──────────────
 *  props.snapshot = null       1st client commit replays    getSnapshot re-reads
 *  cookieSnapshot = null       getServerSnapshot = null     cookie → fresh snapshot
 *  → effective = null          → identical DOM, no warning  → context re-renders,
 *  → HTML: "0 items"                                          badge shows the visitor's
 *                                                             own count
 * ```
 *
 * When the caller passes a real `snapshot`, the same flow applies but `effective` starts as the prop snapshot and is
 * then corrected (or confirmed) by the cookie after hydration.
 *
 * ## Load-bearing invariants
 *
 * These are prerequisites for the cache-safe SSR property above. Breaking any of them removes the guarantee that SSR
 * HTML is safe to share across visitors when the caller passes `snapshot={null}`:
 *
 * - `getServerBasketCookieSnapshot` must return `null`. Any non-null value would be baked into the SSR HTML and
 *   defeat the opt-out.
 * - The cookie-derived snapshot must take precedence over the prop snapshot in the context value
 *   (`cookieSnapshot ?? state.snapshot`). Reversing the order would let a stale or foreign prop snapshot win over
 *   the visitor's own cookie.
 * - The per-provider snapshot cache (`cacheRef` inside the component) is required for referential stability.
 *   `useSyncExternalStore` compares successive `getSnapshot` return values with `Object.is`; without the cache,
 *   every call would allocate a fresh object, fail the comparison, and drive an infinite render loop. The cache
 *   lives on a ref (not module scope) so concurrent SSR requests in the same process cannot observe each other's
 *   state — important even though `getServerSnapshot` always returns `null` today, because it keeps the code
 *   resilient to future changes (and to reviewer scrutiny).
 * - The `getSnapshot` callback passed to `useSyncExternalStore` must itself be stable across renders; a fresh
 *   function reference would force the store to re-subscribe and re-read on every render. The component wraps it
 *   in `useCallback` with empty deps so the ref-backed closure stays identity-stable.
 * - `subscribe` is a noop because cookies emit no events. Re-reads are triggered indirectly by other re-renders
 *   (mutations → `setBasket` → context change → descendants re-render → store snapshot re-read).
 *
 * @param props - Provider props.
 * @param props.children - Children to render within the provider.
 * @param props.basket - Full basket payload when available.
 * @param props.snapshot - Basket snapshot payload when available. Pass `null`
 *   to keep per-visitor state out of the SSR HTML (cache-safe mode); pass the
 *   middleware snapshot to render the counter during SSR (per-user mode).
 * @example
 * ```tsx
 * <BasketProvider snapshot={basketSnapshot}>
 *   <App />
 * </BasketProvider>
 * ```
 *
 * @example
 * ```tsx
 * <BasketProvider basket={basket} snapshot={basketSnapshot}>
 *   <Cart />
 * </BasketProvider>
 * ```
 */
const BasketProvider = (
    props: PropsWithChildren<{ basket?: SCAPIShopperBasketsV2Schemas['Basket']; snapshot?: BasketSnapshot | null }>
) => {
    const { basket, snapshot, children } = props;

    // Providers internal state management.
    const [state, setState] = useState<BasketProviderValue | undefined>(() => {
        if (props.basket === undefined && props.snapshot === undefined) {
            return undefined;
        }

        return {
            current: props.basket,
            snapshot: props.snapshot,
            hydrated: Boolean(props.basket),
            error: null,
        };
    });

    const setBasket = useCallback(
        (next: BasketProviderValue | ((prev: BasketProviderValue | undefined) => BasketProviderValue)) => {
            setState(next);
        },
        []
    );

    // Per-provider cache for the parsed cookie snapshot. useSyncExternalStore compares successive getSnapshot
    // return values via Object.is and would infinite-loop if a fresh object were produced on every read. Keeping
    // the cache on a ref (not module scope) guarantees isolation across concurrent SSR requests — the cache is
    // written to only inside getClientBasketCookieSnapshot, which is only ever invoked on the client today, but
    // ref-scoping keeps the code resilient to future changes.
    const cookieCacheRef = useRef<{ header?: string; snapshot: BasketSnapshot | null }>({ snapshot: null });

    // Stable identity across renders is required: useSyncExternalStore will re-subscribe on every render if the
    // subscribe or getSnapshot references change, which would defeat the Object.is cache.
    const getClientBasketCookieSnapshot = useCallback((): BasketSnapshot | null => {
        const header = document.cookie;
        const cache = cookieCacheRef.current;
        if (header !== cache.header) {
            cache.header = header;
            cache.snapshot = parseBasketCookie(header);
        }
        return cache.snapshot;
    }, []);

    // Post-hydration, prefer the cookie over the props snapshot so a stale server snapshot (e.g. per-user cached HTML
    // that no longer matches the latest basket) is corrected once __sfdc_basket is available.
    //
    // Cross-user leakage on guest-segment CDN hits is NOT prevented here — the `?? state?.snapshot` fallback would
    // still surface another guest's count when the current user has no cookie yet. That leak is prevented at the
    // source: root.tsx emits `null` for guests, so the SSR HTML carries no foreign snapshot to begin with.
    //
    // SSR and the initial hydration commit both return null (matching the server output); useSyncExternalStore
    // permits SSR/client divergence so a post-hydration cookie read does not warn as a hydration mismatch.
    const cookieSnapshot = useSyncExternalStore(
        subscribeBasketCookie,
        getClientBasketCookieSnapshot,
        getServerBasketCookieSnapshot
    );

    const ctxValue = useMemo(() => {
        const effectiveSnapshot = cookieSnapshot ?? state?.snapshot;
        return {
            snapshot: effectiveSnapshot,
            current: state?.current,
            hydrated: state?.hydrated,
            error: state?.error,
        };
    }, [state, cookieSnapshot]);

    // Sync props from the root loader (login/logout/revalidation). useLayoutEffect ensures
    // child hooks (useBasket) see the updated snapshot before their effects run.
    useLayoutEffect(() => {
        if (basket === undefined && snapshot === undefined) {
            return;
        }
        setState((prev) => {
            const nextSnapshot = snapshot === undefined ? prev?.snapshot : snapshot;

            if (basket !== undefined) {
                return {
                    current: basket,
                    snapshot: nextSnapshot,
                    hydrated: Boolean(basket),
                    error: null,
                };
            }

            if (isBasketOutOfSync(prev?.current, nextSnapshot)) {
                return {
                    current: undefined,
                    snapshot: nextSnapshot ?? null,
                    hydrated: false,
                    error: null,
                };
            }

            return {
                current: prev?.current,
                snapshot: nextSnapshot,
                hydrated: prev?.hydrated,
                error: prev?.error ?? null,
            };
        });
    }, [basket, snapshot]);

    const [miniCartOpen, setMiniCartOpen] = useState(false);
    const miniCartValue = useMemo(() => ({ miniCartOpen, setMiniCartOpen }), [miniCartOpen]);

    // Provider-owned basket fetcher. Hosting it here (rather than in a hook called from arbitrary
    // descendants) means a single fetcher instance services every consumer — useBasket auto-loads,
    // usePrefetchCart imperatively loads — and the success/error → context wiring runs exactly
    // once per resolution. Without this centralization each consumer would mount its own fetcher
    // and effect; both would write the same payload (idempotent but wasteful).
    const basketIdForFetcher = ctxValue.snapshot?.basketId ?? '';
    const basketFetcher = useScapiFetcher('shopperBasketsV2', 'getBasket', {
        params: { path: { basketId: basketIdForFetcher } },
    });

    // useScapiFetcher returns a fresh object every render (spread + getters), and basketIdForFetcher
    // can change on prop sync. If loadBasket depended on either, its reference would flip every
    // render, propagate through BasketUpdaterContext, and break consumers that put
    // useBasketUpdater's callback in a useEffect dep array (cart-content, mini-cart, checkout) —
    // they'd loop on setBasket → re-render → new updater callback → effect re-fires.
    // Mirror the live values into refs so loadBasket can use empty deps and stay reference-stable.
    const basketFetcherRef = useRef(basketFetcher);
    basketFetcherRef.current = basketFetcher;
    const basketIdForFetcherRef = useRef(basketIdForFetcher);
    basketIdForFetcherRef.current = basketIdForFetcher;

    // Two refs together produce idempotent loadBasket():
    // - inFlightIdRef: id of the currently-pending request. Cleared in onSuccess/onError so a failure can be retried.
    // - loadedIdRef: id of the most recent successful load. Set in onSuccess so subsequent calls for the same id
    //   (e.g. repeat hover, focus restoration on sheet close) are no-ops.
    // A change in basket id (cookie update, guest→registered handoff) leaves both refs holding the old id; the next
    // load() call for the new id passes the `===` guard naturally and proceeds.
    const inFlightIdRef = useRef<string | null>(null);
    const loadedIdRef = useRef<string | null>(null);

    const loadBasket = useCallback(() => {
        const id = basketIdForFetcherRef.current;
        if (!id || inFlightIdRef.current === id || loadedIdRef.current === id) {
            return;
        }
        inFlightIdRef.current = id;
        void basketFetcherRef.current.load();
    }, []);

    useScapiFetcherEffect(basketFetcher, {
        onSuccess: (data) => {
            inFlightIdRef.current = null;
            // Drop the payload if its basketId no longer matches the active fetcher. Keying off the data's own
            // basketId (rather than inFlightIdRef) means React Router's auto-revalidation after a sibling action
            // submission — e.g. cart-item-update from the mini cart — also writes to context, since those
            // resolutions don't come through loadBasket() and so leave inFlightIdRef null.
            if (!data || data.basketId !== basketIdForFetcherRef.current) {
                return;
            }
            loadedIdRef.current = data.basketId ?? null;
            // Functional update: write only the loaded fields. Do not clobber snapshot — it is
            // owned by props and the cookie store, both of which can be fresher than the value
            // captured when this callback was defined.
            setBasket((prev) => ({
                snapshot: prev?.snapshot,
                current: data,
                hydrated: true,
                error: null,
            }));
        },
        onError: (errors) => {
            const completedId = inFlightIdRef.current;
            inFlightIdRef.current = null;
            // Only surface errors that originated from an explicit loadBasket() call. An auto-revalidation failure
            // (inFlightIdRef === null) must not overwrite a previously-good basket with an error state — the
            // sibling action that triggered the revalidation owns its own error reporting.
            if (!completedId || completedId !== basketIdForFetcherRef.current) {
                return;
            }
            setBasket((prev) => ({
                snapshot: prev?.snapshot,
                current: prev?.current,
                hydrated: true,
                error: errors,
            }));
        },
    });

    const updaterValue = useMemo(() => ({ setBasket, loadBasket }), [setBasket, loadBasket]);

    return (
        <BasketUpdaterContext.Provider value={updaterValue}>
            <MiniCartContext.Provider value={miniCartValue}>
                <BasketContext.Provider value={ctxValue}>{children}</BasketContext.Provider>
            </MiniCartContext.Provider>
        </BasketUpdaterContext.Provider>
    );
};

/**
 * Returns the imperative basket loader exposed by {@link BasketProvider}. Calling this triggers the provider-owned
 * fetcher (or no-ops if a fetch already happened or is in flight). The result is written to context by the provider —
 * observe it via {@link useBasket}.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useBasketLoader = (): (() => void) => {
    const updater = useContext(BasketUpdaterContext);
    return useCallback(() => {
        updater?.loadBasket();
    }, [updater]);
};

/**
 * Returns the current basket from context. By default, this hook is **read-only** — it doesn't trigger any SCAPI
 * request. Routes that need the basket on first paint are expected to either (a) load it in their server `loader` and
 * write it to context via `useBasketUpdater()` in a `useLayoutEffect` (cart, checkout), or (b) opt in to auto-load by
 * passing `{ autoLoad: true }`.
 *
 * Pass `{ autoLoad: true }` only on routes that genuinely need a client-side `GET /baskets/{id}` (e.g. PDP edit-mode
 * where the basket isn't loader-hydrated). When auto-load is enabled and `current` is missing but a `snapshot.basketId`
 * is available, the provider-owned fetcher is dispatched and the result is written back to context.
 *
 * This default keeps consumers from issuing redundant GETs — and keeps the SSR HTML cache-safe, because nothing fans
 * out an opportunistic basket fetch on hydration unless the consumer asks for it.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useBasket = (options?: { autoLoad?: boolean }): SCAPIShopperBasketsV2Schemas['Basket'] | undefined => {
    const autoLoad = options?.autoLoad ?? false;
    const { current, snapshot } = useContext(BasketContext);
    const loadBasket = useBasketLoader();
    const basketId = snapshot?.basketId;

    // basketId is in the dep array so a hand-off (e.g. guest → registered merge changes the id)
    // re-fires the load. loadBasket is reference-stable so it never spuriously re-triggers.
    useEffect(() => {
        if (autoLoad && !current && basketId) {
            loadBasket();
        }
    }, [autoLoad, current, basketId, loadBasket]);

    return current;
};

/**
 * Returns the current basket snapshot, if available.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useBasketSnapshot = (): BasketSnapshot | null | undefined => {
    return useContext(BasketContext).snapshot;
};

const BASKET_LINE_READY: Promise<void> = Promise.resolve();

type PendingLineReadiness = { basketId: string; resolve: () => void; promise: Promise<void> };

/**
 * Whether the full basket has been fetched at least once.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useBasketLineReadiness = (): Promise<void> => {
    const { snapshot, hydrated } = useContext(BasketContext);
    const basketId = (snapshot?.basketId ?? '').trim();
    const pendingRef = useRef<PendingLineReadiness | null>(null);

    const promise = useMemo((): Promise<void> => {
        if (!basketId || hydrated === true) {
            return BASKET_LINE_READY;
        }
        if (!pendingRef.current) {
            let resolve!: () => void;
            const p = new Promise<void>((r) => {
                resolve = r;
            });
            pendingRef.current = { basketId, promise: p, resolve };
        }
        return pendingRef.current.promise;
    }, [basketId, hydrated]);

    useLayoutEffect(() => {
        if (hydrated === true && pendingRef.current) {
            pendingRef.current.resolve();
            pendingRef.current = null;
        }
    }, [hydrated]);

    useLayoutEffect(() => {
        if (!basketId && pendingRef.current) {
            pendingRef.current.resolve();
            pendingRef.current = null;
        }
    }, [basketId]);

    useLayoutEffect(
        () => () => {
            if (pendingRef.current) {
                pendingRef.current.resolve();
                pendingRef.current = null;
            }
        },
        [basketId]
    );

    return promise;
};

/** Whether the full basket has been fetched at least once. */
// eslint-disable-next-line react-refresh/only-export-components
export const useBasketHydrated = (): boolean => {
    return useContext(BasketContext).hydrated ?? false;
};

/**
 * Returns a setter for updating the basket in context.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useBasketUpdater = (): ((basket?: SCAPIShopperBasketsV2Schemas['Basket']) => void) => {
    const updater = useContext(BasketUpdaterContext);
    return useCallback(
        (basket?: SCAPIShopperBasketsV2Schemas['Basket']) => {
            updater?.setBasket((prev) => {
                // Dedup: Skip the update, when called with a defined basket whose SCAPI-set `lastModified` matches
                // the basket already in context. The undefined-basket path (clear-and-rehydrate) is unaffected —
                // callers that want to wipe `current` while keeping `hydrated: true` still work.
                if (basket?.lastModified && prev?.current?.lastModified === basket.lastModified && prev?.hydrated) {
                    const giftCardUnchanged = prev.current.c_hasGiftCardProduct === basket.c_hasGiftCardProduct;
                    const billingUnchanged =
                        JSON.stringify(prev.current.billingAddress ?? null) ===
                        JSON.stringify(basket.billingAddress ?? null);
                    if (giftCardUnchanged && billingUnchanged) {
                        return prev;
                    }
                }
                return {
                    current: basket,
                    hydrated: true,
                    snapshot: basket ? defaultCreateSnapshot(basket) : undefined,
                    error: null,
                };
            });
        },
        [updater]
    );
};

/**
 * Returns a callback that clears the basket context when invoked.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useBasketReset = (): (() => void) => {
    const updater = useContext(BasketUpdaterContext);
    return useCallback(() => {
        updater?.setBasket({
            current: undefined,
            snapshot: undefined,
            hydrated: false,
            error: null,
        });
    }, [updater]);
};

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noopSetMiniCartOpen: (open: boolean) => void = () => {};
const fallbackMiniCartValue: MiniCartProviderValue = {
    miniCartOpen: false,
    setMiniCartOpen: noopSetMiniCartOpen,
};

// eslint-disable-next-line react-refresh/only-export-components
export const useMiniCart = (): MiniCartProviderValue => {
    return useContext(MiniCartContext) ?? fallbackMiniCartValue;
};

export default BasketProvider;
